/**
 * Responsibility: manage where alerts go.
 *
 * A subscription is a named channel that claims a set of tags. Any watch
 * carrying one of those tags alerts to it, on top of the hub's global
 * channels - routing here is additive, never exclusive, so a mis-tagged watch
 * reaches fewer places than intended rather than none at all.
 *
 * Rotation is the operation this page exists for. An ntfy topic cannot be
 * revoked, only replaced, so a topic that has been seen by the wrong person is
 * fixed by minting a new one and resubscribing.
 */
import { useState } from 'react';
import { PageContainer, SectionCard } from '@kit/components';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SendIcon from '@mui/icons-material/SendOutlined';
import RotateIcon from '@mui/icons-material/AutorenewOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import PauseIcon from '@mui/icons-material/PauseCircleOutline';
import PlayIcon from '@mui/icons-material/PlayCircleOutline';
import { NotifyListPicker } from '../components/ui/NotifyListPicker';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { SecretRevealDialog } from '../components/notify/SecretRevealDialog';
import {
  useCreateSubscription,
  useDeleteSubscription,
  usePatchSubscription,
  useRotateSubscription,
  useSubscriptions,
  useNotifyLists,
  useTestSubscription,
} from '../api/hooks/useNotify';
import type { SecretReveal, Subscription } from '../api/client';

function when(value: string | null): string {
  if (!value) return 'never';
  return new Date(value).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function AlertsPage() {
  const { data: subscriptions, error } = useSubscriptions();
  const { data: knownLists } = useNotifyLists();

  const create = useCreateSubscription();
  const rotate = useRotateSubscription();
  const patch = usePatchSubscription();
  const test = useTestSubscription();
  const remove = useDeleteSubscription();

  const [name, setName] = useState('');
  const [notifyLists, setNotifyLists] = useState<string[]>([]);
  const [url, setUrl] = useState('');
  const [reveal, setReveal] = useState<SecretReveal | null>(null);
  const [pendingRemoval, setPendingRemoval] = useState<Subscription | null>(null);
  const [pendingRotation, setPendingRotation] = useState<Subscription | null>(null);

  const list = subscriptions ?? [];

  const add = () => {
    create.mutate(
      { name: name.trim(), notify_lists: notifyLists, url: url.trim() || null },
      {
        onSuccess: (result) => {
          setReveal(result);
          setName('');
          setNotifyLists([]);
          setUrl('');
        },
      },
    );
  };

  return (
    <PageContainer
    >
      {/* See WatchlistPage: a flex item defaults to min-width:auto, so anything
          with a large intrinsic width would widen this column and slide the
          whole page sideways on a phone. */}
      <Stack spacing={3} sx={{ minWidth: 0 }}>

      {error ? <Alert severity="error">{(error as Error).message}</Alert> : null}
      {test.isError ? <Alert severity="error">{(test.error as Error).message}</Alert> : null}
      {test.isSuccess ? <Alert severity="success">Test sent. Check your phone.</Alert> : null}

      <SectionCard
        title="New channel"
        subtitle="Leave the URL empty and a private ntfy topic is generated for you."
      >
        <Stack spacing={2.5}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Name"
              placeholder="My phone"
              value={name}
              onChange={(event) => setName(event.target.value)}
              fullWidth
            />
            <TextField
              label="Existing URL (optional)"
              placeholder="ntfys://ntfy.sh/an-existing-topic"
              value={url}
              onChange={(event) => setUrl(event.target.value)}
              fullWidth
              helperText="Any Apprise URL - ntfy, email, Discord, Telegram. Held on the hub and never shown back to you."
            />
          </Stack>

          <NotifyListPicker
            value={notifyLists}
            onChange={setNotifyLists}
            options={knownLists ?? []}
          />

          {create.isError ? (
            <Alert severity="error">{(create.error as Error).message}</Alert>
          ) : null}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button size="small"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={add}
              disabled={name.trim() === '' || create.isPending}
            >
              {create.isPending ? 'Creating' : 'Create channel'}
            </Button>
          </Box>
        </Stack>
      </SectionCard>

      <SectionCard title="Channels">
        {list.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No channels yet. Your hub-wide ntfy and email settings still receive everything;
            these are for routing a particular notify list somewhere specific.
          </Typography>
        ) : (
          <Stack spacing={2}>
            {list.map((subscription) => (
              <Box
                key={subscription.id}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  opacity: subscription.enabled ? 1 : 0.55,
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ mb: 1 }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {subscription.name}
                  </Typography>
                  <Chip size="small" variant="outlined" label={subscription.kind} />
                  <Typography variant="caption" color="text.secondary">
                    {subscription.masked_target}
                  </Typography>
                  {!subscription.enabled ? <Chip size="small" label="paused" /> : null}

                  <Box sx={{ flexGrow: 1 }} />

                  <span>
                    <IconButton
                      size="small"
                      disabled={test.isPending}
                      onClick={() => test.mutate(subscription.id)}
                    >
                      <SendIcon fontSize="small" />
                    </IconButton>
                  </span>

                  <span>
                    <IconButton
                      size="small"
                      disabled={!subscription.rotatable || rotate.isPending}
                      onClick={() => setPendingRotation(subscription)}
                    >
                      <RotateIcon fontSize="small" />
                    </IconButton>
                  </span>

                  <span>
                    <IconButton
                      size="small"
                      onClick={() =>
                        patch.mutate({
                          id: subscription.id,
                          patch: { enabled: !subscription.enabled },
                        })
                      }
                    >
                      {subscription.enabled ? (
                        <PauseIcon fontSize="small" />
                      ) : (
                        <PlayIcon fontSize="small" />
                      )}
                    </IconButton>
                  </span>

                  <span>
                    <IconButton size="small" onClick={() => setPendingRemoval(subscription)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </span>

                </Stack>

                <NotifyListPicker
                  value={subscription.notify_lists}
                  onChange={(next) =>
                    patch.mutate({ id: subscription.id, patch: { notify_lists: next } })
                  }
                  options={knownLists ?? []}
                  label="Sends these lists"
                  helperText={`Last sent ${when(subscription.last_sent_at)}`}
                />
              </Box>
            ))}
          </Stack>
        )}
      </SectionCard>

      <SecretRevealDialog reveal={reveal} onClose={() => setReveal(null)} />

      <ConfirmDialog
        open={pendingRotation !== null}
        title="Issue a new topic?"
        body={
          pendingRotation
            ? `${pendingRotation.name} will get a brand new topic immediately. The current one stops receiving alerts at once, so you will need to resubscribe your phone. Anyone who knows the old topic loses access - which is the point.`
            : ''
        }
        confirmLabel="Rotate"
        pending={rotate.isPending}
        onCancel={() => setPendingRotation(null)}
        onConfirm={() => {
          if (!pendingRotation) return;
          rotate.mutate(pendingRotation.id, {
            onSuccess: (result) => {
              setReveal(result);
              setPendingRotation(null);
            },
          });
        }}
      />

      <ConfirmDialog
        open={pendingRemoval !== null}
        title="Delete this channel?"
        body={
          pendingRemoval
            ? `${pendingRemoval.name} will stop receiving alerts. Products on ${pendingRemoval.notify_lists.join(', ') || 'its lists'} keep alerting to your default channels.`
            : ''
        }
        confirmLabel="Delete"
        pending={remove.isPending}
        onCancel={() => setPendingRemoval(null)}
        onConfirm={() => {
          if (!pendingRemoval) return;
          remove.mutate(pendingRemoval.id, { onSuccess: () => setPendingRemoval(null) });
        }}
      />
      </Stack>
    </PageContainer>
  );
}
